import React, { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cdpService from '../../../../apiService/cdp.service';
import {
    formatCustomerCurrency,
    formatCustomerDate,
    formatCustomerValue,
    getStatusColor,
} from './customerSubscriptionUtils';

const DetailItem = ({ label, value }) => (
    <div className="flex flex-row items-center gap-2 border border-gray-300 px-2 py-1">
        <p className="text-xs font-medium tracking-wide text-gray-600">{label} : </p>
        <p className="mt-0 break-all text-xs font-medium text-gray-800">{formatCustomerValue(value)}</p>
    </div>
);

const formatLabel = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

const SUMMARY_KEYS = ["product", "plan_name", "payment_status", "subscription_status","amount_paid", "payment_type", "type", "currency", "scheme", "sell_price", "payment_date", "billing_cycle", "duration_type", "status"];
const PLAN_VALIDITY_KEYS = ["start_date", "end_date"];
const PAYMENT_REFERENCE_KEYS = ["orderId", "transaction_id", "razorpay_id", "razorpay_sub_id"];
const EXCLUDED_KEYS = ["purchase_id", "bundle_id", "length"];
const DATE_KEYS = ["payment_date", "created_at", "updated_at"];

const isPrimitive = (value) => value === null || typeof value !== "object";

const canSendInvoice = (purchase) => {
    const transactionId = purchase?.transaction_id || purchase?.payment?.transaction_id;
    const paymentStatus = String(purchase?.payment_status || "").toLowerCase();
    return Boolean(transactionId) && paymentStatus === "success";
};

const InvoiceButton = ({ status, onClick }) => {
    const state = status?.state;
    const baseClass = "inline-flex min-w-[7.75rem] items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold";

    if (state === "sending") {
        return (
            <button type="button" disabled className={`${baseClass} cursor-wait bg-gray-100 text-gray-600`}>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
                Sending
            </button>
        );
    }

    if (state === "success") {
        return (
            <span className={`${baseClass} bg-green-600 text-white`}>
                <CheckIcon className="h-4 w-4" />
                Sent
            </span>
        );
    }

    return (
        <button
            type="button"
            onClick={onClick}
            className={`${baseClass} border border-gray-300 text-gray-700 hover:bg-gray-50`}
        >
            Send Invoice
        </button>
    );
};

const PurchaseDetailFields = ({ data, topLevel = false }) => {
    if (!data || typeof data !== "object") {
        return <p className="text-sm text-gray-700">{formatCustomerValue(data)}</p>;
    }

    const entries = Object.entries(data);
    const summaryEntries = topLevel ? entries.filter(([key]) => SUMMARY_KEYS.includes(key)) : [];
    const planValidityEntries = topLevel ? entries.filter(([key]) => PLAN_VALIDITY_KEYS.includes(key)) : [];
    const paymentReferenceEntries = topLevel ? entries.filter(([key]) => PAYMENT_REFERENCE_KEYS.includes(key)) : [];
    const remainingEntries = topLevel
        ? entries.filter(
            ([key]) =>
                !SUMMARY_KEYS.includes(key) &&
                !EXCLUDED_KEYS.includes(key) &&
                !PLAN_VALIDITY_KEYS.includes(key) &&
                !PAYMENT_REFERENCE_KEYS.includes(key)
                
        )
        : entries;
    const flatEntries = remainingEntries.filter(([, value]) => isPrimitive(value));
    const nestedEntries = remainingEntries.filter(([, value]) => !isPrimitive(value));

    return (
        <div className="space-y-2">
            {summaryEntries.length > 0 ? (
                <div className="grid grid-cols-2 gap-1 bg-gray-50 p-2 sm:grid-cols-3">
                    {summaryEntries.map(([key, value]) => (
                        <DetailItem
                            key={key}
                            label={formatLabel(key)}
                            value={DATE_KEYS.includes(key) ? formatCustomerDate(value, true) : value}
                        />
                    ))}
                </div>
            ) : null}

            {planValidityEntries.length > 0 ? (
                <section className="p-2">
                    <h4 className="mb-1 text-sm font-bold text-gray-900">Plan Validity</h4>
                    <div className="flex flex-row flex-wrap items-center gap-3">
                        {planValidityEntries.map(([key, value]) => (
                            <DetailItem
                                key={key}
                                label={formatLabel(key)}
                                value={formatCustomerDate(value, true)}
                            />
                        ))}
                    </div>
                </section>
            ) : null}

            {paymentReferenceEntries.length > 0 ? (
                <section className="p-2">
                    <h4 className="mb-1 text-sm font-bold text-gray-900">Payment References</h4>
                    <div className="flex flex-row flex-wrap items-center gap-3">
                        {paymentReferenceEntries.map(([key, value]) => (
                            <DetailItem key={key} label={formatLabel(key)} value={value} />
                        ))}
                    </div>
                </section>
            ) : null}

            {flatEntries.length > 0 ? (
                <div className="flex flex-row flex-wrap items-center gap-3">
                    {flatEntries.map(([key, value]) => (
                        <DetailItem
                            key={key}
                            label={formatLabel(key)}
                            value={DATE_KEYS.includes(key) ? formatCustomerDate(value, true) : value}
                        />
                    ))}
                </div>
            ) : null}

            {nestedEntries.map(([key, value]) => {
                if (Array.isArray(value)) {
                    const isPrimitiveList = value.every((item) => isPrimitive(item));
                    return (
                        <section key={key} className="p-2">
                            <h4 className="mb-1 text-sm font-bold text-gray-900">{formatLabel(key)}</h4>
                            {value.length === 0 ? (
                                <p className="text-sm text-gray-500">No data</p>
                            ) : isPrimitiveList ? (
                                <div className="flex flex-row flex-wrap items-center gap-2">
                                    {value.map((item, index) => (
                                        <span
                                            key={`${key}-${index}`}
                                            className="rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
                                        >
                                            {formatCustomerValue(item)}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {value.map((item, index) => (
                                        <div key={`${key}-${index}`} className=" bg-gray-50 p-3">
                                            <p className="text-xs font-semibold text-gray-900">{item?.product_name || "-"}</p>
                                            <p className="mt-1 text-xs text-gray-500">{item?.domain_url || "-"}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    );
                }

                return (
                    <section key={key} className="rounded-lg border border-gray-200 p-2">
                        <h4 className="mb-1 text-sm font-bold text-gray-900">{formatLabel(key)}</h4>
                        <PurchaseDetailFields data={value} />
                    </section>
                );
            })}
        </div>
    );
};

const CustomerSubscriptionHistory = ({
    history,
    loading,
    pageSize,
    onPageChange,
    onPageSizeChange,
}) => {
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [purchaseDetail, setPurchaseDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState("");
    const [invoiceStatus, setInvoiceStatus] = useState({});
    const rows = Array.isArray(history) ? history : history?.dataList || [];
    const currentPage = Array.isArray(history) ? 1 : history?.currentPage || 1;
    const totalPages = Array.isArray(history) ? 1 : Math.max(history?.totalPages || 1, 1);
    const totalRecords = Array.isArray(history) ? rows.length : history?.totalRecords || 0;

    console.log("purchaseDetail state:", purchaseDetail);

    const openPurchaseDetail = async (purchase) => {
        setSelectedPurchase(purchase);
        setPurchaseDetail(null);
        setDetailError("");
        setDetailLoading(true);
        try {
            const response = await cdpService.getCustomerPurchaseHistoryDetail(purchase.id);
            setPurchaseDetail(
                response.data?.data?.reportData ??
                response.data?.data?.purchaseHistory ??
                response.data?.data ??
                response.data
            );
        } catch (error) {
            setDetailError(
                error.response?.data?.message || "Unable to load purchase details."
            );
        } finally {
            setDetailLoading(false);
        }
    };

    console.log("CustomerSubscriptionHistory rows:", rows);

    const sendInvoice = async (purchase, event) => {
        event.stopPropagation();
        const transactionId = purchase.transaction_id || purchase.payment?.transaction_id;
        if (!canSendInvoice(purchase)) return;
        if (!window.confirm("Send the invoice email for this purchase?")) return;

        setInvoiceStatus((current) => ({
            ...current,
            [purchase.id]: { state: "sending", message: "Sending..." },
        }));
        try {
            const response = await cdpService.sendInvoice(transactionId);
            const body = response.data;
            setInvoiceStatus((current) => ({
                ...current,
                [purchase.id]: {
                    state: body?.success ? "success" : "error",
                    message: body?.message || (body?.success ? "Email sent" : "Unable to send invoice."),
                },
            }));
        } catch (error) {
            setInvoiceStatus((current) => ({
                ...current,
                [purchase.id]: {
                    state: "error",
                    message: error.response?.data?.message || "Unable to send invoice.",
                },
            }));
        }
    };

    const closePurchaseDetail = () => {
        setSelectedPurchase(null);
        setPurchaseDetail(null);
        setDetailError("");
    };

    return (
        <>
        <div className="rounded-xl bg-white shadow-sm ring-1 ring-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-6 py-5">
                <div>
                    <h2 className="text-sm font-bold text-gray-900">Purchase History</h2>
                    <p className="mt-0 text-xs text-gray-500">{totalRecords} purchase records</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600">
                    Show
                    <select
                        value={pageSize}
                        onChange={(event) => onPageSizeChange(Number(event.target.value))}
                        className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none"
                    >
                        {[10, 25, 50, 100].map((size) => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            {["Purchase ID", "Plan", "Payment Type", "Transaction ID", "Status", "Price", "Payment Date", "Action"].map((heading) => (
                                <th key={heading} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {heading}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">Loading purchase history...</td>
                            </tr>
                        ) : null}
                        {!loading && rows.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">No purchase history found</td>
                            </tr>
                        ) : null}
                        {!loading && rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => openPurchaseDetail(row)}
                                className="cursor-pointer hover:bg-blue-50/50"
                            >
                                <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-blue-600">{row.id || "-"}</td>
                                <td className="whitespace-nowrap px-4 py-4 text-sm font-medium text-gray-900">
                                    {row.plan_name || row.plan_details?.subscription_name || "-"}
                                </td>
                                <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">{row.payment_type || row.payment?.payment_type || "-"}</td>
                                <td className="max-w-56 truncate px-4 py-4 text-sm text-gray-600">{row.transaction_id || row.payment?.transaction_id || "-"}</td>
                                <td className="whitespace-nowrap px-4 py-4">
                                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(row.payment_status)}`}>
                                        {(row.payment_status || "Unknown").toUpperCase()}
                                    </span>
                                </td>
                                <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatCustomerCurrency(row.sell_price ?? row.billing?.sell_price, row.billing?.currency || "INR")}
                                </td>
                                <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                                    {formatCustomerDate(row.payment_date || row.payment?.created_at, true)}
                                </td>
                                <td className="whitespace-nowrap px-4 py-4">
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                openPurchaseDetail(row);
                                            }}
                                            className="rounded-md bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700"
                                        >
                                            View Details
                                        </button>
                                        {canSendInvoice(row) ? (
                                            <InvoiceButton
                                                status={invoiceStatus[row.id]}
                                                onClick={(event) => sendInvoice(row, event)}
                                            />
                                        ) : null}
                                    </div>
                                    {invoiceStatus[row.id]?.state === "error" ? (
                                        <p className="mt-1 text-xs text-red-600">{invoiceStatus[row.id].message}</p>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
                <p className="text-sm text-gray-500">
                    Page {currentPage} of {totalPages}
                </p>
                <div className="flex gap-2">
                    <button
                        type="button"
                        disabled={loading || !history?.previousPage}
                        onClick={() => onPageChange(currentPage - 1)}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        disabled={loading || !history?.nextPage}
                        onClick={() => onPageChange(currentPage + 1)}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
        <Dialog open={Boolean(selectedPurchase)} onClose={closePurchaseDetail} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />
            <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
                <div className="flex min-h-full items-center justify-center">
                    <DialogPanel className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-2">
                            <div>
                                <DialogTitle className="text-sm font-bold text-gray-900">Purchase Details</DialogTitle>
                                <p className="mt-0 text-xs text-gray-500">Purchase ID: {selectedPurchase?.id || "-"}</p>
                            </div>
                            <button
                                type="button"
                                onClick={closePurchaseDetail}
                                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                aria-label="Close purchase details"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
                            {detailLoading ? (
                                <p className="py-10 text-center text-sm text-gray-500">Loading purchase details...</p>
                            ) : null}
                            {!detailLoading && detailError ? (
                                <p className="rounded-lg bg-red-50 p-4 text-sm text-red-700">{detailError}</p>
                            ) : null}
                            {!detailLoading && !detailError && purchaseDetail ? (
                                <PurchaseDetailFields data={purchaseDetail} topLevel />
                            ) : null}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
        </>
    );
};

export default CustomerSubscriptionHistory;
